/* ─────────────────────────────────────────────
   PITCH MATH (LEDGER-LINE READY)
   Single source of truth for staff ↔ pitch
   ───────────────────────────────────────────── */

export const STAFF_TOP = 40;
export const LINE_GAP = 10;

/* ───────── CLEF BASE REFERENCES ─────────
   Index 0 = bottom line of staff
*/

const CLEF_BASE = {
  treble: {
    pitch: "E4", // bottom line
  },
  bass: {
    pitch: "G2", // bottom line
  }
};

const LETTERS = ["C", "D", "E", "F", "G", "A", "B"];

/* ───────── INTERNAL HELPERS ───────── */

function cleanPitch(pitch) {
  if (!pitch) return pitch;
  return pitch.replace(/[#bxfn]/g, "");
}

function pitchToDiatonic(pitch) {
  pitch = cleanPitch(pitch);
  const letter = pitch[0];
  const octave = parseInt(pitch.match(/\d+/)?.[0], 10);

  return octave * 7 + LETTERS.indexOf(letter);
}

function diatonicToPitch(d) {
  const letter = LETTERS[(d % 7 + 7) % 7];
  const octave = Math.floor(d / 7);
  return `${letter}${octave}`;
}

/* ───────── PUBLIC API ───────── */

/**
 * Convert pitch → staff index
 * 0 = bottom line
 * Can be negative or >8 (ledger lines)
 */
export function pitchToStaffIndex(pitch, clef) {
  const basePitch = CLEF_BASE[clef]?.pitch;
  if (!basePitch) return 0;

  return (
    pitchToDiatonic(pitch) -
    pitchToDiatonic(basePitch)
  );
}

/**
 * Convert staff index → Y position
 */
export function staffIndexToY(index) {
  const baseY = STAFF_TOP + 4 * LINE_GAP; // bottom line
  return baseY - index * (LINE_GAP / 2);
}

/**
 * Convert pitch → Y position
 * (ledger-safe)
 */
// export function pitchToY(pitch, clef) {
//   const index = pitchToStaffIndex(pitch, clef);
//   return staffIndexToY(index);
// }

export function pitchToY(
  pitch,
  clef,
  options = {}
) {
  const {
    staffTop = STAFF_TOP   // backward-compatible
  } = options;

  const index = pitchToStaffIndex(pitch, clef);
  const baseY = staffTop + 4 * LINE_GAP;

  return baseY - index * (LINE_GAP / 2);
}


/**
 * Snap mouse Y → nearest pitch
 * (ledger-safe)
 */
// export function snapYToPitch(y, clef, options = {}) {
//   const {
//     allowLedgerLines = true
//   } = options;

//   const baseY = STAFF_TOP + 4 * LINE_GAP;
//   let index = Math.round((baseY - y) / (LINE_GAP / 2));

//   // Optional clamp (for restricted-range questions)
//   if (!allowLedgerLines) {
//     index = Math.max(-1, Math.min(index, 9));
//   } else {
//     index = Math.max(-5, Math.min(index, 13));
//   }

//   return staffIndexToPitch(index, clef);
// }
export function snapYToPitch(
  y,
  clef,
  options = {}
) {
  const {
    allowLedgerLines = true,
    staffTop = STAFF_TOP   // ← default for existing widgets
  } = options;

  const baseY = staffTop + 4 * LINE_GAP;
  let index = Math.round((baseY - y) / (LINE_GAP / 2));

  if (!allowLedgerLines) {
    index = Math.max(-1, Math.min(index, 9));
  } else {
    index = Math.max(-5, Math.min(index, 13));
  }

  return staffIndexToPitch(index, clef);
}


/**
 * Convert staff index → pitch string
 */
export function staffIndexToPitch(index, clef) {
  const basePitch = CLEF_BASE[considerClef(clef)]?.pitch;
  if (!basePitch) return null;

  const baseD = pitchToDiatonic(basePitch);
  return diatonicToPitch(baseD + index);
}

/* ───────── SAFETY ───────── */

function considerClef(clef) {
  return clef === "bass" ? "bass" : "treble";
}
